<template>
  <div class="pb-1">
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <div>
        <b-row>
          <b-col md="6">
            <FormulateInput
              id="delivery-supplier-name"
              v-model="filters.name"
              :label="$t('Nome do fornecedor')"
              :placeholder="$t('Nome')"
            />
          </b-col>
          <b-col md="6">
            <FormulateInput
              id="delivery-supplier-active"
              v-model="filters.active"
              type="vue-select"
              :label="$t('Status')"
              :placeholder="$t('Todos')"
              :options="activeInactiveTypes()"
            />
          </b-col>
        </b-row>
      </div>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <div slot="title">
        <e-page-size-selector
          :per-page="paging.pageSize"
          @change="pageSizeChange"
        />
      </div>

      <b-table
        ref="delivery-suppliers-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($t('Fornecedor de delivery'), 'male', queryExecuted)"
        :fields="fields"
        :items="deliverySuppliers"
        no-local-sorting
        :sort-by.sync="sorting.sortBy"
        :sort-desc.sync="sorting.sortDesc"
        @sort-changed="getData"
      >
        <template #cell(supplier)="row">
          {{ deliverySupplierLabel[row.item.supplier] || row.item.supplier }}
        </template>

        <template #cell(active)="row">
          <e-status-badge :status="row.item.active" />
        </template>

        <template #cell(action)="row">
          <e-grid-actions
            :is-active="row.item.active"
            :show-update="$can('Update', 'DeliverySupplier')"
            :show-delete="false"
            :show-activate="$can('Activate', 'DeliverySupplier')"
            :show-deactivate="$can('Deactivate', 'DeliverySupplier')"
            @update="showUpdateSidebar(row)"
            @activate="activateDeactivate(row)"
            @deactivate="activateDeactivate(row)"
          />
        </template>
      </b-table>

      <b-row>
        <b-col cols="6">
          <e-pagination-summary
            :current-page="paging.currentPage"
            :per-page="paging.pageSize"
            :total="paging.rowCount"
            :total-on-page="paging.rowsInCurrentPage"
          />
        </b-col>
        <b-col cols="6">
          <b-pagination
            v-model="paging.currentPage"
            align="right"
            :total-rows="paging.rowCount"
            :per-page="paging.pageSize"
            aria-controls="delivery-suppliers-table"
            @change="pageChange"
          />
        </b-col>
      </b-row>
    </b-card-actions>

    <delivery-supplier-sidebar
      ref="delivery_supplier_sidebar"
      @save="getData"
    />

    <fab
      v-if="$can('Create', 'DeliverySupplier')"
      :main-tooltip="$t('Adicionar Fornecedor de Delivery')"
      @click="showCreateSidebar"
    />
  </div>
</template>

<script>
import { BRow, BCol, BPagination, BTable } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { mapActions, mapState } from 'vuex'
import { deliveryDomain, statusTypes } from '@/mixins'
import {
  EPageSizeSelector,
  EPaginationSummary,
  EStatusBadge,
  EFilters,
  EGridActions,
} from '@/views/components'
import fab from '@/views/components/FAB.vue'
import DeliverySupplierSidebar from './DeliverySupplierSidebar.vue'

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BPagination,
    BCardActions,
    EPaginationSummary,
    EPageSizeSelector,
    EStatusBadge,
    EFilters,
    EGridActions,
    DeliverySupplierSidebar,
    fab,
  },

  mixins: [deliveryDomain, statusTypes],

  data() {
    return {
      fetching: false,
      queryExecuted: false,
    }
  },

  computed: {
    ...mapState('pages/sale/deliverySupplier', {
      deliverySuppliers: 'deliverySuppliers',
      paging: 'paging',
      sorting: 'sorting',
      filters: 'filters',
    }),

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Id'),
          key: 'id',
          class: 'text-center',
          thStyle: { width: '100px' },
          sortable: true,
        },
        {
          label: this.$t('Nome'),
          key: 'name',
          thClass: 'text-center',
          tdClass: 'text-left',
          sortable: true,
        },
        {
          label: this.$t('Integração'),
          key: 'supplier',
          class: 'text-center',
          thStyle: { width: '200px' },
          sortable: true,
        },
        {
          label: this.$t('Status'),
          key: 'active',
          class: 'text-center',
          thStyle: { width: '90px' },
          sortable: true,
        },
      ]
    },
  },

  mounted() {
    this.getData()
  },

  methods: {
    ...mapActions('pages/sale/deliverySupplier', [
      'fetchDeliverySuppliers',
      'setCurrentPage',
      'setPageSize',
      'resetFilters',
    ]),

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchDeliverySuppliers()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    filterData() {
      this.setCurrentPage(1)
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },

    pageSizeChange(pageSize) {
      this.setPageSize(pageSize)
      this.getData()
    },

    pageChange(currentPage) {
      this.setCurrentPage(currentPage)
      this.getData()
    },

    showCreateSidebar() {
      this.$refs.delivery_supplier_sidebar.show()
    },

    showUpdateSidebar(row) {
      this.$refs.delivery_supplier_sidebar.show({ id: row.item.id })
    },

    async activateDeactivate(row) {
      try {
        const confirm = await this.confirm()
        if (confirm) {
          const path = row.item.active ? 'deactivate' : 'activate'
          await this.$http({
            url: `/api/sales/delivery-suppliers/${path}/${row.item.id}`,
            method: 'PUT',
          })
          this.getData()
          this.showSuccess({
            message: this.$t(`${row.item.active ? 'Inativado' : 'Ativado'} com sucesso`),
          })
        }
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>

<style></style>
